import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  AsyncLoadingSection,
  DisplayBlock,
  ModalDrawerButtonRow,
  MultiModalFormDrawer,
  Render,
} from 'Components/Display';
import {
  ButtonStyle,
  FormSubmitButton,
  IconButton,
  ModalFormCancelButton,
  SmartFileUploader,
} from 'Components/Form';
import {
  FormActionTypes,
  useEntity,
  useFormState,
  useFormUpdater,
  useGridState,
  useModalHelpers,
} from 'Context';
import { AppointmentLetterModal, AppointmentLetterModel } from 'Models/AppointmentLetterModel';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import React, { useEffect, useState } from 'react';
import { Row } from 'reactstrap';
import {
  buildEndpoint,
  EMPTY_GUID,
  useDataFetcher,
} from 'Utilities';

export type AppointmentLetterModalFormProps = {
  endpoint?: string;
  formModel?: AppointmentLetterModel;
};

export const AppointmentLetterDrawerForm: React.FC<
  AppointmentLetterModalFormProps
> = ({ formModel, ...props }) => {
  const entity = useEntity();
  formModel = new AppointmentLetterModel(
    entity.appointment?.id
  );
  const { simpleSuccessHandler } = useModalHelpers(
    AppointmentLetterModal.Attachments
  );
  const { refreshGrid } = useGridState();

  const handleSuccess = () => {
    simpleSuccessHandler('Appointment Letter has been updated.');
    refreshGrid();
  };

  return (
    <MultiModalFormDrawer
      modalId={AppointmentLetterModal.Attachments}
      submitButtonText="Save"
      formModel={formModel}
      endpoint={props.endpoint}
      handleSuccess={handleSuccess}
    >
      <AppointmentLetterDrawerFormContent {...props} />
    </MultiModalFormDrawer>
  );
};

export const AppointmentLetterDrawerFormContent: React.FC<
  AppointmentLetterModalFormProps
> = ({ ...props }) => {
  const entity = useEntity();
  const { isOpen, entityId } = useModalHelpers(
    AppointmentLetterModal.Attachments
  );
  const sendUpdate = useFormUpdater();
  const { formValues } = useFormState();
  const [fileAdd, setIsAdd] = useState(entityId === undefined);
  const isAdd = entityId === undefined;

  const removeTemplateFile = () => {
    setIsAdd(!isAdd);
    sendUpdate(FormActionTypes.UpdateValue, { field: 'fileId', value: null });
  };

  let [appointmentLetterModel, fetchStatus] =
    useDataFetcher<AppointmentLetterModel>(
      buildEndpoint(
        ENDPOINTS.appointmentletters.base,
        entityId ?? EMPTY_GUID
      ),
      new AppointmentLetterModel(entity.id),
      ({ data }) => data,
      undefined,
      isOpen
    );

  useEffect(() => {
    if (!isAdd) {
      sendUpdate(FormActionTypes.SetValues, appointmentLetterModel);
    }
  }, [appointmentLetterModel, sendUpdate]);

  return (
    <AsyncLoadingSection loadingStatus={fetchStatus} noIdleLoading>
      <Render condition={!fileAdd && (formValues?.fileId || false)}>
        <Row>
          <IconButton
            iconElement={HighlightOffIcon}
            primary
            buttonStyle={ButtonStyle.Bare}
            onClick={removeTemplateFile}
          />
          <DisplayBlock value={formValues?.fileName} title="File" />
        </Row>
      </Render>
      <SmartFileUploader
        label="Files"
        required
        fileEndpoint={ENDPOINTS.appointmentletters.base}
        isEdit={!fileAdd}
        vertical
        withComments={false}
        allowMultiple={false}
      />
      <ModalDrawerButtonRow>
        <ModalFormCancelButton />
        <FormSubmitButton>Save Letter</FormSubmitButton>
      </ModalDrawerButtonRow>
    </AsyncLoadingSection>
  );
};